import { ref, Ref, watchEffect } from 'vue';
import { AxiosError } from 'axios';
import Bugsnag from '@bugsnag/js';
import { useI18n } from 'vue-i18n';
import { ApiDataFetchingState, useApiStore } from '@/store/api';

/**
 * Wraps a fetch function to use the api store to keep loading and error state.
 *
 * **Important** This function can only be called from inside the Vue component constructor or <script setup> block.
 */
export function usingApiStore<T extends Array<unknown>, U>(fetch: (...any: T) => Promise<U>) {
  const { setApiDateFetchingState } = useApiStore();
  return async (...args: T): Promise<U> => {
    setApiDateFetchingState(ApiDataFetchingState.Fetching);
    try {
      const result = await fetch(...args);
      setApiDateFetchingState(ApiDataFetchingState.Fetched);
      return result;
    } catch (e) {
      if (e instanceof AxiosError && e.response?.status === 404) {
        setApiDateFetchingState(ApiDataFetchingState.ContentNotFound);
      } else {
        setApiDateFetchingState(ApiDataFetchingState.UnexpectedError);
        Bugsnag.notify(e as AxiosError);
      }
      throw e;
    }
  };
}

/**
 * Utility function that allows us to create a ref that will contain the data once the fetch has returned.
 * If the passed function contains references or reactive objects, the data will be fetched again if they change.
 *
 * **Important** This function can only be called from inside the Vue component constructor or <script setup> block.
 */
export function useAsyncRef<T>(fetch: () => Promise<T>): Ref<T | null> {
  const reference: Ref<T | null> = ref(null);

  // re-fetch data when ref change
  watchEffect(onCleanup => {
    reference.value = null;

    let cancelled = false;
    onCleanup(() => {
      cancelled = true;
    });

    reference.value = null;
    const loadData = async () => {
      const data = await fetch();
      if (!cancelled) {
        reference.value = data;
      }
    };
    loadData().catch(error => {
      // eslint-disable-next-line no-console
      console.error('[useAsyncRef] unhandled fetch error (ref stays null)', error);
    });
  });

  return reference;
}

/**
 * Utility function that allows us to create a ref that will contain the data once the API call has returned.
 * If the passed function contains references or reactive objects, the data will be fetched again if they change.
 *
 * **Important** This function can only be called from inside the Vue component constructor or <script setup> block.
 */
export function apiDataRef<T>(fetch: () => Promise<T>): Ref<T | null> {
  return useAsyncRef(usingApiStore(fetch));
}

/**
 * Utility function that allows us to create a ref that will contain the data once the API call has returned.
 * If the passed function contains references or reactive objects, the data will be fetched again if they change.
 * In addition to the above behavior, this function will reload the data if the locale is changed.
 *
 * **Important** This function can only be called from inside the Vue component constructor or <script setup> block.
 */
export function apiDataWithLocaleRef<T>(fetch: (lang: string) => Promise<T>): Ref<T | null> {
  const i18n = useI18n();
  return apiDataRef(() => fetch(i18n.locale.value));
}
